/**
 * Single source of truth for all route metadata. Pure data — no React, no JSX,
 * no browser APIs — so vite.config.ts can import it via relative path for chunk naming.
 *
 * `sourcePath` must match the literal string in the corresponding lazy() call exactly.
 * Vite requires static import() strings; sourcePath cannot be used as the import() argument.
 */

export type RouteManifestEntry = {
  routePath: string;
  /** Rollup chunk name. Used as componentMap key in route config files. */
  chunkName: string;
  /** Must match the literal in lazy(() => import('<sourcePath>')). Keep in sync manually. */
  sourcePath: string;
  role?: string;
  skipProfileFetchPath?: boolean;
};

type ProtectedRouteManifestEntry = RouteManifestEntry & { role: string };

// ── Protected routes ──────────────────────────────────────────────────────────

export const PROTECTED_ROUTE_MANIFEST = [
  {
    routePath: '/agent/dashboard/*',
    chunkName: 'route-agent-dashboard',
    sourcePath: 'app/pages/Agent/Dashboard',
    role: 'agent',
  },
  {
    routePath: '/compliance/dashboard/*',
    chunkName: 'route-compliance-dashboard',
    sourcePath: 'app/pages/Compliance/Dashboard',
    role: 'compliance',
  },
  {
    routePath: '/compliance/work-items-preview/*',
    chunkName: 'route-work-item-preview',
    sourcePath: 'app/modules/DailyHub/WorkItemPreviewPage',
    role: 'compliance',
  },
  {
    routePath: '/office-admin/dashboard/*',
    chunkName: 'route-office-admin',
    sourcePath: 'app/pages/OfficeAdmin',
    role: 'officeAdmin',
  },
  {
    routePath: '/admin/management',
    chunkName: 'route-management',
    sourcePath: 'app/pages/Admin/ManagementPage',
    role: 'admin',
  },
  {
    routePath: '/agent/email-labels/completed',
    chunkName: 'route-email-labels-auth-completed',
    sourcePath: 'app/pages/Agent/Onboarding/EmailLabelsAuthCompleted',
    role: 'agent',
  },
  {
    routePath: '/agent/dashboard/transactions/detail',
    chunkName: 'route-transaction-page',
    sourcePath: 'app/AgentDetails/TransactionPage',
    role: 'agent',
  },
  {
    // Shell-agnostic: same URLs for compliance, office-admin and agent users.
    routePath: '/configs/*',
    chunkName: 'route-configs',
    sourcePath: 'app/pages/Configs',
    role: 'compliance',
  },
  {
    routePath: '/transaction',
    chunkName: 'route-entity-redirect',
    sourcePath: 'app/components/common/EntityRedirect',
    role: 'agent',
  },
] as const satisfies ProtectedRouteManifestEntry[];

export type ProtectedChunkName =
  (typeof PROTECTED_ROUTE_MANIFEST)[number]['chunkName'];

// ── Public routes ─────────────────────────────────────────────────────────────

export const PUBLIC_ROUTE_MANIFEST = [
  {
    routePath: '/',
    chunkName: 'route-landing',
    sourcePath: 'app/pages/LandingPage',
  },
  {
    routePath: '/get-in-touch',
    chunkName: 'route-get-in-touch',
    sourcePath: 'app/pages/LandingPage/GetInTouch',
  },
  {
    routePath: '/agent/login',
    chunkName: 'route-agent-login',
    sourcePath: 'app/pages/Agent/Login/AgentLoginPage',
  },
  {
    routePath: '/compliance/login',
    chunkName: 'route-compliance-login',
    sourcePath: 'app/pages/Compliance/Login',
  },
  {
    routePath: '/agent/onboarding/create-account',
    chunkName: 'route-agent-onboarding-create-account',
    sourcePath: 'app/pages/Agent/Onboarding/CreateAccountPage',
  },
  {
    routePath: '/agent/onboarding/add-email',
    chunkName: 'route-agent-onboarding-add-email',
    sourcePath: 'app/pages/Agent/Onboarding/AddEmailPage',
  },
  {
    routePath: '/agent/email-labels',
    chunkName: 'route-email-labels-auth',
    sourcePath: 'app/pages/Agent/Onboarding/EmailLabelsAuthPage',
  },
  {
    routePath: '/agent/onboarding/completed',
    chunkName: 'route-agent-onboarding-completed',
    sourcePath: 'app/pages/Agent/Onboarding/CompletedPage',
  },
  {
    routePath: '/signature/:slug',
    chunkName: 'route-signature',
    sourcePath: 'app/pages/SignatureRoute',
    skipProfileFetchPath: true,
  },
  {
    routePath: '/privacy-policy',
    chunkName: 'route-privacy-policy',
    sourcePath: 'app/pages/Shared/PrivacyPolicy',
  },
  {
    routePath: '/terms-and-conditions',
    chunkName: 'route-terms-and-conditions',
    sourcePath: 'app/pages/Shared/TermsAndConditions',
  },
  {
    routePath: '/careers',
    chunkName: 'route-careers',
    sourcePath: 'app/pages/CareersPage',
  },
  {
    routePath: '/confirmation',
    chunkName: 'route-confirmation',
    sourcePath: 'app/pages/Confirmation/ConfirmationScreen',
    skipProfileFetchPath: true,
  },
  {
    routePath: '/new-listing/:formId',
    chunkName: 'route-new-listing',
    sourcePath: 'app/pages/NewListing',
    skipProfileFetchPath: true,
  },
  {
    routePath: '/sales-form/:formId',
    chunkName: 'route-sales-form',
    sourcePath: 'app/pages/SalesForm',
    skipProfileFetchPath: true,
  },
  {
    routePath: '/deal-sheet/:transactionId',
    chunkName: 'route-deal-sheet',
    sourcePath: 'app/pages/DealSheet',
    skipProfileFetchPath: true,
  },
  {
    routePath: '/lease-deal-sheet/:transactionId',
    chunkName: 'route-lease-deal-sheet',
    sourcePath: 'app/pages/LeaseDealSheet',
    skipProfileFetchPath: true,
  },
  {
    routePath: '/redirect/*',
    chunkName: 'route-entity-redirect',
    sourcePath: 'app/components/common/EntityRedirect',
  },
  {
    routePath: '/agent-upload/:uploadKey',
    chunkName: 'route-agent-upload',
    sourcePath: 'app/pages/AgentUpload/AgentUploadPage',
    skipProfileFetchPath: true,
  },
  {
    routePath: '/calendar/:token',
    chunkName: 'route-calendar-link',
    sourcePath: 'app/pages/CalendarLink/CalendarLinkPage',
    skipProfileFetchPath: true,
  },
  {
    routePath: '/compliance/:transactionId/documents/:documentId',
    chunkName: 'route-bw-document-view',
    sourcePath: 'app/pages/BWDocumentView',
    skipProfileFetchPath: true,
  },
] as const satisfies RouteManifestEntry[];

export type PublicChunkName =
  (typeof PUBLIC_ROUTE_MANIFEST)[number]['chunkName'];
